import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { adminApi }  from '../api/adminApi'
import { PostForm }  from '../components/editor/PostForm'
import { Button }    from '../components/ui/Button'
import { Spinner }   from '../components/ui/Spinner'
import { toast }     from '../store/adminStore'
import type { Post } from '../types'

export function PostEditPage() {
  const { slug } = useParams<{ slug?: string }>()
  const isNew = !slug

  const [post,      setPost]      = useState<Post | null>(null)
  const [loading,   setLoading]   = useState(!isNew)
  const [error,     setError]     = useState<string | null>(null)
  const [notifying, setNotifying] = useState(false)

  useEffect(() => {
    if (isNew) return
    setLoading(true)
    adminApi.getPost(slug!)
      .then((res) => setPost(res))
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'Failed to load post'
        setError(msg)
        toast.error(msg)
      })
      .finally(() => setLoading(false))
  }, [slug, isNew])

  return (
    <>
      {/* Breadcrumb */}
      <nav className="pep-breadcrumb">
        <Link to="/posts" className="pep-bc-link">Posts</Link>
        <span className="pep-bc-sep">›</span>
        <span className="pep-bc-current">{isNew ? 'New Post' : (post?.title ?? slug)}</span>
      </nav>

      <div className="page-header" style={{ marginTop: 12 }}>
        <div>
          <h1 className="page-title">{isNew ? 'Create New Post' : 'Edit Post'}</h1>
          <p className="page-subtitle">
            {isNew
              ? 'Fill in the fields below. Examples are shown next to each control.'
              : `Editing /${slug}`}
          </p>
        </div>
        {!isNew && post?.status === 'published' && (
          <Button
            variant="secondary"
            size="sm"
            loading={notifying}
            onClick={async () => {
              if (!slug) return
              setNotifying(true)
              try {
                const { dispatched } = await adminApi.notifySubscribers(slug)
                toast.success(`Notification sent to ${dispatched} subscriber${dispatched !== 1 ? 's' : ''}.`)
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Notification failed')
              } finally {
                setNotifying(false)
              }
            }}
          >
            ✉ Notify Subscribers
          </Button>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <Spinner label="Loading post…" />
        </div>
      ) : error ? (
        <div className="pep-error">
          <p>{error}</p>
          <Link to="/posts" className="pep-bc-link">← Back to Posts</Link>
        </div>
      ) : (
        <div className="pep-body">
          <PostForm initial={post ?? undefined} />
        </div>
      )}

      <style>{`
        .pep-breadcrumb {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
        }
        .pep-bc-link {
          color: var(--accent);
          text-decoration: none;
          font-weight: 500;
        }
        .pep-bc-link:hover { text-decoration: underline; }
        .pep-bc-sep { color: var(--text-muted); }
        .pep-bc-current { color: var(--text-secondary); }
        .pep-body {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          padding: 24px 28px;
          margin-top: 4px;
        }
        .pep-error {
          padding: 40px;
          text-align: center;
          color: var(--color-error);
          font-size: 0.9rem;
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }
      `}</style>
    </>
  )
}
